import React from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
} from "react-native";
import { HorizontalScrollView } from "../ui/HorizontalScrollView";
import { Select } from "../ui/Select";
import { Chip } from "../ui/Chip";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { spacing } from "../../theme";
import type { Filters } from "../../types/filters";
import { formatDisplayValue } from "../../utils/format";

// Format price range values for display
const formatPriceRange = (value: string): string => {
  if (value.startsWith("0-")) {
    return `Under $${value.split("-")[1]}`;
  }
  if (value.endsWith("+")) {
    return `Over $${value.replace("+", "")}`;
  }
  const [min, max] = value.split("-");
  return `$${min} - $${max}`;
};

interface ProductFilterPanelProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  sortBy: string;
  onSort: (value: string) => void;
}

export function ProductFilterPanel({
  filters,
  onFiltersChange,
  sortBy,
  onSort,
}: ProductFilterPanelProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== undefined
  );

  // Handle clearing all filters
  const handleClearFilters = () => {
    onFiltersChange({
      condition: undefined,
      size: undefined,
      priceRange: undefined,
      main_category: undefined,
      availability: undefined,
    });
  };

  return (
    <View style={styles.container}>
      <HorizontalScrollView
        contentContainerStyle={styles.filtersContent}
        persistId="product-filter-panel"
      >
        <Select
          value={filters.main_category || ""}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              main_category: value || undefined,
            })
          }
          options={[
            { label: "Women's Fashion", value: "womens-fashion" },
            { label: "Men's Fashion", value: "mens-fashion" },
            { label: "Kids", value: "kids" },
          ]}
          placeholder="Category"
        />

        <Select
          value={filters.availability || ""}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              availability: (value as 'open' | 'closed') || undefined,
            })
          }
          options={[
            { label: "Open Now", value: "open" },
            { label: "Closed", value: "closed" },
          ]}
          placeholder="Availability"
        />

        <Select
          value={filters.condition || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, condition: value || undefined })
          }
          options={[
            { label: "New", value: "new" },
            { label: "Like New", value: "like_new" },
            { label: "Good", value: "good" },
            { label: "Fair", value: "fair" },
          ]}
          placeholder="Condition"
        />

        <Select
          value={filters.size || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, size: value || undefined })
          }
          options={[
            { label: "XS", value: "xs" },
            { label: "S", value: "s" },
            { label: "M", value: "m" },
            { label: "L", value: "l" },
            { label: "XL", value: "xl" },
          ]}
          placeholder="Size"
        />

        <Select
          value={filters.priceRange || ""}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, priceRange: value || undefined })
          }
          options={[
            { label: "Under $25", value: "0-25" },
            { label: "$25 - $50", value: "25-50" },
            { label: "$50 - $100", value: "50-100" },
            { label: "Over $100", value: "100+" },
          ]}
          placeholder="Price Range"
        />

        <Select
          value={sortBy}
          onValueChange={onSort}
          options={[
            { label: "Newest", value: "newest" },
            { label: "Price: Low to High", value: "price_asc" },
            { label: "Price: High to Low", value: "price_desc" },
          ]}
          placeholder="Sort By"
        />
      </HorizontalScrollView>

      {hasActiveFilters && (
        <View style={styles.activeFiltersRow}>
          <View style={styles.activeFilters}>
            {Object.entries(filters).map(([key, value]) =>
              value ? (
                <Chip
                  key={key}
                  label={`${key === "main_category" || key === "category_subcategory" || key === "category_item" ? "Category" : key === "priceRange" ? "Price Range" : key === "availability" ? "Availability" : formatDisplayValue(key)}: ${key === "priceRange" ? formatPriceRange(value) : key === "availability" ? (value === "open" ? "Open Now" : "Closed") : formatDisplayValue(value)}`}
                  onRemove={() =>
                    onFiltersChange({
                      ...filters,
                      [key]: undefined,
                    })
                  }
                />
              ) : null
            )}
          </View>
          <TouchableOpacity
            style={styles.clearButton}
            onPress={handleClearFilters}
          >
            <Feather name="x" size={14} color={colors.primary} />
            <Text style={styles.clearButtonText}>Clear All</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      paddingTop: 8,
      paddingBottom: 4,
      backgroundColor: colors.background,
    },
    filtersContent: {
      paddingHorizontal: spacing.lg,
      gap: 10,
      flexDirection: "row",
    },
    activeFiltersRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginTop: 16,
      paddingHorizontal: spacing.lg,
    },
    activeFilters: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
      flex: 1,
      marginRight: 12,
    },
    clearButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingTop: 4,
    },
    clearButtonText: {
      fontSize: 13,
      color: colors.primary,
      fontWeight: "600",
    },
  });
